import styled, { css } from "styled-components";
import { borderRadius, boxShadow, color, focusRing, highContrastStyle, localReset, onHover, supportsHasSelector, transition, typeStyle, units, variant, visuallyHidden } from "@m/alchemy-theme";
import { NakedButton } from "../../naked-button/NakedButton";
import { ArrowDownIcon } from "../../icons/ArrowDownIcon";
import { DisplayBox } from "../../display-box/DisplayBox";
import { Checkbox } from "../checkbox/Checkbox";
import { inputFocusRing, inputHoverRing, inputTransition, themedInputBackground } from "../input/Input.styled";
import { Button } from "../../button/Button.styled";
import { ScreenReaderContent } from "../../screen-reader-content/ScreenReaderContent";
import { SelectedIcon } from "../../menu-list-item/MenuListItem.styled";
import { ArrowRightIcon } from "../../icons/ArrowRightIcon";
export const Wrapper = styled.div `
  position: relative;
  max-width: 100%;
  ${({ isFullWidth }) => 
/* IE 11 requires this to be flex
  https://stackoverflow.com/questions/19371626/flexbox-not-centering-vertically-in-ie */
isFullWidth ? "display: flex; width: 100%;" : "display: inline-flex;"};
  select {
    ${visuallyHidden()};
  }
`;
export const MenuDisplayBox = styled(DisplayBox) `
  box-shadow: ${boxShadow("menu")};
  border-radius: ${borderRadius("extraLarge")};
  overflow: visible;
  border: none;
`;
export const highContrastBackground = css `
  background: linear-gradient(to right, ${themedInputBackground}, ${themedInputBackground}),
    linear-gradient(180deg, ${color("line")} 0%, ${color("base", { palette: "expressiveNeutral" })} 100%);
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
`;
export const defaultBackground = css `
  background: ${themedInputBackground};
`;
export const Toggle = styled.button `
  font-size: var(--font-size, 14px);
  font-family: inherit;
  font-weight: normal;
  cursor: ${({ "aria-disabled": ariaDisabled }) => (ariaDisabled ? "not-allowed" : "pointer")};
  opacity: ${({ "aria-disabled": ariaDisabled }) => (ariaDisabled ? 0.3 : 1)};
  min-height: var(--input-height, 32px);
  display: inline-flex;
  align-items: center;
  color: ${color("typeBody")};
  line-height: 1;
  position: relative;
  padding: ${units(0.375)} max(${units(6)}, calc(var(--end-adornment-width, 0px) + ${units(4)})) ${units(0.375)}
    max(${units(2)}, calc(var(--start-adornment-width, 0px) - 4px));
  border: 1px solid ${color("inputBorder")};
  border-radius: ${borderRadius("full")};
  ${highContrastStyle(highContrastBackground, defaultBackground)};
  ${inputTransition};
  box-shadow:
    0 1px 1px 0 rgba(0, 0, 0, 0.02),
    0 2px 3px 0 rgba(0, 0, 0, 0.05);

  &:hover,
  &:focus {
    border-color: ${color("base")};
    ${(props) => inputHoverRing(props.palette)};
  }
  &:focus {
    box-shadow:
      0 1px 1px 0 rgba(0, 0, 0, 0.02),
      0 2px 3px 0 rgba(0, 0, 0, 0.05),
      /** from inputFocusRing */ 0px 0px 0px 1px ${color("base", { palette: "interactiveConstructive" })} inset;
  }

  ${({ isOpen, palette }) => (isOpen ? inputFocusRing(palette) : "")}
  ${({ isFullWidth }) => (isFullWidth ? "width: 100%;" : "max-width: 100%;")}

  ${({ hasStatus, palette }) => hasStatus &&
    css `
      border-color: ${color("base")};
      ${inputFocusRing(palette)};
    `};
`;
export const TextWrapper = styled.div `
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
export const Arrow = styled(ArrowDownIcon) `
  right: ${units(2)};
  color: ${color("base")};
  transition: ${transition()};
  position: absolute;
  top: 50%;
  line-height: 0;
  transform: translateY(-50%);
  z-index: 1;
  ${({ isOpen }) => (isOpen ? `transform: translateY(-50%) rotate(-180deg);` : "")};
`;
export const Item = styled.li.attrs(({ isSelectable }) => ({
    isSelectable: isSelectable ?? true
})) `
  ${localReset()}
  box-sizing: border-box;
  outline: revert;

  color: ${color("typeBody")};
  position: relative;
  display: flex;
  gap: ${units(1)};
  margin: 0;
  text-align: left;
  transition: background-color 0.1s ease;
  padding: ${({ hasCheckboxes }) => (hasCheckboxes ? 0 : `${units(1)} ${units(2)}`)};
  ${typeStyle("bodyS")};
  cursor: pointer;

  [data-whatinput="touch"] & {
    padding-top: ${units(1.5)};
    padding-bottom: ${units(1.5)};
  }

  /* Safari workaround to fix disappearing focus rings on first and 
     last items when overflow: auto or hidden 
     https://codepen.io/greypants/pen/LYQwYmZ?editors=1100
  */
  transform: translateZ(0);
  width: 100%;

  ${({ isSelected }) => isSelected &&
    css `
      background-color: ${color("hint", { palette: "interactiveConstructive" })};
    `}

  ${({ isNested, depth }) => isNested &&
    css `
      ${Label} {
        padding-left: ${depth && depth > 1 ? units(3.5 * (depth - 1)) : 0};
      }
    `};

  ${({ isFirst }) => isFirst &&
    css `
      border-top-left-radius: ${borderRadius("extraLarge")};
      border-top-right-radius: ${borderRadius("extraLarge")}; // TODO: detect scrollbars and set to 0
    `};

  ${({ isLast }) => isLast &&
    css `
      border-bottom-left-radius: ${borderRadius("extraLarge")};
      border-bottom-right-radius: ${borderRadius("extraLarge")}; // TODO: detect scrollbars and set to 0
    `};

  ${({ isDisabled }) => isDisabled &&
    css `
      color: ${color("typeMeta")};
      cursor: not-allowed;
    `};

  ${({ isHighlighted, ...props }) => isHighlighted && `background-color: ${color("hint")(props)};`}

  ${supportsHasSelector(css `
      [data-whatinput="keyboard"] &:has([data-select-item]:focus),
      [data-whatinput="keyboard"] &[data-select-item]:focus {
        ${focusRing({ type: "inner" })}
        background-color: ${
// one of the few instances where color modifiers are actually OK https://una.im/color-mix-opacity/
color("hint", { opacity: 0.65, palette: "interactiveConstructive" })};
      }
    `, {
    fallbackStyles: css `
        [data-whatinput="keyboard"] &:focus,
        [data-whatinput="keyboard"] &:focus-within {
          ${focusRing({ type: "inner" })}
          background-color: ${color("hint", { opacity: 0.65, palette: "interactiveConstructive" })};
        }
      `
})}

  ${SelectedIcon} {
    align-self: center;
  }

  ${({ isDisabled, isSelectable }) => isDisabled || !isSelectable
    ? css ``
    : css `
          &:hover {
            background-color: ${color("hint", { opacity: 0.65, palette: "interactiveConstructive" })};
          }
        `}

  /** formatting for filtered items in in OptionListHandler */
  em {
    transition: background-color 0.1s ease;
    background-color: ${color("highlight")};
  }

  ${variant("as", {
    span: css `
      cursor: revert;
      display: block;
      background-color: ${color("container")};
      padding-left: 0;

      ${onHover(css `
        background-color: ${color("container")};
      `)}
    `
})}
`;
export const OptionItem = styled(Item).attrs(({ role }) => ({
    as: "button",
    type: "button",
    role: role ?? "option"
})) ``;
export const Label = styled.div `
  display: inline-flex;
  flex-direction: column;
`;
// keeping this in simply for SelectOption & SelectOptionCheckbox optionProps type
export const Option = styled.div ``;
export const OptionCheckboxLabel = styled.span `
  display: inline-block;
  padding: ${units(1)} ${units(2)} ${units(1)} 0;
  width: 100%;
`;
export const OptionCheckbox = styled(Checkbox) `
  ${({ isParent }) => isParent &&
    css `
      span {
        font-weight: bold;
      }
    `};
`;
export const MenuListWrapper = styled.div `
  overflow: hidden;
  overflow-y: auto;
  position: relative;
  border-radius: ${borderRadius("extraLarge")};

  ${({ isSearchable, hasCheckboxes }) => (isSearchable || hasCheckboxes) &&
    css `
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    `};

  ${({ hasFooter }) => hasFooter &&
    css `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `};

  /** TODO remove style overrides, allow native scrollbars */
  ::-webkit-scrollbar {
    border-radius: 0 16px 16px 0;
    width: 12px;
  }

  ::-webkit-scrollbar-track {
    border-left: 2px solid #fff;
    border-top: 2px solid #fff;
    background-color: #fff;
    border-radius: ${({ isSearchable }) => (isSearchable ? `0 0 ${units(2)} 0` : `0 ${units(2)} ${units(2)} 0`)};
  }

  ::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: ${units(2)};
    border: 3px solid #fff;
  }
`;
export const MenuList = styled.ul `
  display: inline-block;
  padding: 0;
  width: 100%;
`;
export const Legend = styled(ScreenReaderContent).attrs({ as: "legend" }) ``;
export const ForwardButtonWrapper = styled.span `
  margin-left: auto;
  display: flex;
  justify-content: flex-end;
  position: relative;
  flex-grow: 0;
`;
export const IconWrapper = styled.span `
  border-radius: 4px;
  border: 1px solid transparent;
  margin: 0 ${units(0.5)};
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: ${transition("base", "background-color")};
  border-radius: ${borderRadius("extraLarge")};
`;
export const ForwardButton = styled(NakedButton) `
  position: relative;
  top: 50%;
  right: -2px;
  line-height: 0;
  height: 100%;
  transform: translateY(-50%);
  outline: none; // applied to IconWrapper below instead

  display: flex;
  justify-content: flex-end;

  flex-grow: 1;

  ${onHover(css `
    ${IconWrapper} {
      background-color: ${color("highlight", { opacity: 0.4, palette: "interactiveConstructive" })};
      color: ${color("base", { palette: "interactiveConstructive" })};
    }
  `)}

  [data-whatinput="keyboard"] &:focus ${IconWrapper} {
    ${focusRing({ type: "native" })}
  }
`;
export const ForwardIcon = styled(ArrowRightIcon) `
  margin-left: auto;
  align-self: center;
  justify-self: center;
`;
export const OptionCheckboxItem = styled(Item) `
  display: flex;
  justify-content: space-between;
  align-content: center;
  padding-left: ${units(2)};
  margin-right: 2px;

  ${({ isNested, depth }) => isNested &&
    css `
      padding-left: calc(${units(2)} + ${depth ? units(3.5 * depth) : units(3.5)});
    `};

  ${ForwardButton} {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`;
export const ApplyContainer = styled.div `
  display: flex;
  padding: ${units(1)} ${units(1)} ${units(1.5)};

  ${Button} {
    width: 100%;
    margin: 0 ${units(0.5)};
  }
`;
